import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

const ticketConfirmationBlockParams = {
  registryId: PUI.REGISTRY_IDS.GUEST_FORM_PAGE,
  blockKey: PUI.GUEST_FORM_BLOCK_KEYS.TICKET_CONFIRMATION,
} as const;

/**
 * Large success check icon above the title
 */
export const useTicketSuccessIcon = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.TICKET_SUCCESS_ICON,
  });

  const iconColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    isVisible,
    iconColor,
  };
};

/**
 * Composable for ticket confirmation title customisation
 */
export const useTicketTitle = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.TICKET_TITLE,
  });

  const titleText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const titleTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    titleText,
    titleTextColor,
  };
};

/**
 * Composable for ticket ID label text customisation
 */
export const useTicketIdText = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.TICKET_ID_TEXT,
  });

  const text = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    text,
    textColor,
  };
};

/**
 * Styling for the dynamic ticket ID value
 */
export const useTicketIdValue = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.TICKET_ID_VALUE,
  });

  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    textColor,
  };
};

/**
 * Composable for created ticket confirmation text customisation
 */
export const useTicketCreatedText = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.CREATED_TICKET_TEXT,
  });

  const text = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    text,
    textColor,
  };
};

/**
 * Composable for ticket next steps title customisation
 */
export const useTicketNextStepsTitle = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NEXT_STEPS_TITLE,
  });

  const titleText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const titleTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    titleText,
    titleTextColor,
  };
};

/**
 * Check icon color for next-step list items
 */
export const useTicketNextStepsStepIcon = () => {
  const { getField } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NEXT_STEPS_STEP_ICON,
  });

  const iconColor = getField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR);

  return {
    iconColor,
  };
};

/**
 * Composable for ticket next steps customisation
 * Returns customisation for all three next steps
 */
export const useTicketNextSteps = () => {
  const { getField: getStep1Field } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NEXT_STEP_1,
  });

  const step1Text = getStep1Field(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const step1TextColor = getStep1Field(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  const { getField: getStep2Field } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NEXT_STEP_2,
  });

  const step2Text = getStep2Field(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const step2TextColor = getStep2Field(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  const { getField: getStep3Field } = useCreateFieldGetter({
    ...ticketConfirmationBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NEXT_STEP_3,
  });

  const step3Text = getStep3Field(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const step3TextColor = getStep3Field(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    step1Text,
    step1TextColor,
    step2Text,
    step2TextColor,
    step3Text,
    step3TextColor,
  };
};

/**
 * Combined composable that returns all ticket confirmation customisations
 */
export const useTicketConfirmationAllCustomisation = () => {
  const successIcon = useTicketSuccessIcon();
  const title = useTicketTitle();
  const idText = useTicketIdText();
  const idValue = useTicketIdValue();
  const createdText = useTicketCreatedText();
  const nextStepsTitle = useTicketNextStepsTitle();
  const nextStepsStepIcon = useTicketNextStepsStepIcon();
  const nextSteps = useTicketNextSteps();

  return {
    successIcon,
    title,
    idText,
    idValue,
    createdText,
    nextStepsTitle,
    nextStepsStepIcon,
    nextSteps,
  };
};
